// ═══════════════════════════════════════════════════════════════════════════
//  PNL MATH — the ONE place a ledger row is turned into money (God Phase 3,
//  2026-09-24: Paper / Live Auto Trading per-trade upgrade).
//
//  Every surface that shows Running M2M, Booked or Total P&L for the running
//  bots (Open Positions panel, Granular Position Manager, header tiles) calls
//  these functions. Two copies of this arithmetic is how a card once showed one
//  P&L while the panel beside it showed another, so there is exactly one copy.
//
//  Formulas (God's order, verbatim):
//    OPTION BUY row  (long)             M2M = (liveLtp − entryPrice) × openQty
//    OPTION SELL-to-open row (written)  M2M = (entryPrice − liveLtp) × openQty
//    FUTURES / CASH                     analogous by direction (LONG +1, SHORT −1)
//    Booked  = Σ netPnl (fallback pnl) of CLOSED rows (WIN / LOSS / BREAKEVEN)
//    Total   = Booked + Σ M2M of OPEN rows that HAVE a live price
//
//  Nothing here fabricates a number: a row with no live tick yields `null`, and
//  the caller renders "—" with a reason. A `null` is never coerced to 0 by this
//  module — 0 is a real P&L and "unknown" is not.
//
//  No test runner exists in this repo (package.json has no jest/vitest), so
//  `pnlMathSelfCheck()` below is the executable spec: it runs the worked
//  examples in the comment table and returns the failures by name. Call it from
//  the browser console (`window.__pnlMathSelfCheck()` is exposed in DEV builds)
//  or from any future test file.
// ═══════════════════════════════════════════════════════════════════════════

export type AssetKind = 'OPTION' | 'FUTURE' | 'CASH';

/** The subset of a `tradeorders` ledger row this module reads. Every field is
 *  optional on purpose — rows written before a field existed carry `undefined`,
 *  and the helpers must answer honestly for them. */
export interface PnlRow {
  id?: string;
  botId?: string;
  symbol?: string;          // underlying, e.g. "NIFTY_50"
  optionSymbol?: string;    // "NIFTY_50 23250 CE" / "SILVERM FUT"
  option_symbol?: string;   // the same field as /trades/open spells it
  contractSymbol?: string;  // "BANK_NIFTY 2026-09-29 55500 CE"
  strike?: number | string;
  optType?: string;         // CE | PE | ''
  assetClass?: string;      // OPTION | FUTURE | STOCK | '' (often blank on FUT rows)
  side?: string;            // BUY | SELL (entry side on most rows)
  entrySide?: string;
  direction?: string;       // LONG | SHORT
  qty?: number | string;
  quantity?: number | string;
  openQty?: number | string;
  entryPrice?: number | string;
  entry_price?: number | string;
  exitPrice?: number | string;
  status?: string;
  voided?: boolean;
  pnl?: number | string;
  netPnl?: number | string;
  dnaSl?: number | string | null;          // structural stop on the UNDERLYING
  dnaTarget?: number | string | null;      // structural target on the UNDERLYING
  dnaSlPremium?: number | string | null;   // the same stop expressed in option PREMIUM
  dnaSlPremiumBasis?: string | null;       // CHAIN_DELTA_x / ATM_ASSUMED_DELTA_0.50
  expiry?: string | null;
  expiryDate?: string | null;
  timeframe?: string;
  securityId?: string | number;
  [k: string]: unknown;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const up = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/**
 * What instrument the row traded. `assetClass` is read first but it is BLANK on
 * the futures rows the ledger holds today (measured 2026-09-24: SILVERM FUT row
 * carries assetClass ''), so the option type and the contract name decide when
 * it is missing. A CE/PE row is an option no matter what else it says.
 */
export function assetKindOf(row: PnlRow): AssetKind {
  const ot = up(row.optType);
  if (ot === 'CE' || ot === 'PE') return 'OPTION';
  const ac = up(row.assetClass);
  if (ac === 'OPTION') return 'OPTION';
  if (ac === 'FUTURE' || ac === 'FUT' || ac === 'FUTURES') return 'FUTURE';
  if (ac === 'STOCK' || ac === 'CASH' || ac === 'EQUITY' || ac === 'ETF') return 'CASH';
  const os = up(row.optionSymbol || row.option_symbol || row.contractSymbol);
  if (/\b(CE|PE)$/.test(os)) return 'OPTION';
  if (/\bFUT(URES?)?$/.test(os) || /_FUT$/.test(os)) return 'FUTURE';
  return 'CASH';
}

/** +1 = long / BUY-to-open, −1 = SHORT / SELL-to-open (a written option, a
 *  short future). Read from the row, never inferred from the bot. */
export function dirOf(row: PnlRow): 1 | -1 {
  if (up(row.direction) === 'SHORT') return -1;
  if (up(row.direction) === 'LONG') return 1;
  const side = up(row.entrySide || row.side);
  return side === 'SELL' ? -1 : 1;
}

/** Units still open. Falls back to qty ONLY for a genuinely open row that has
 *  no openQty stamped; closed rows are 0 regardless of stale residue. */
export function openQtyOf(row: PnlRow): number {
  const st = up(row.status);
  const open = st === 'OPEN' || st === 'PARTIALLY_CLOSED';
  if (!open || row.voided) return 0;
  const oq = row.openQty != null && row.openQty !== '' ? num(row.openQty) : num(row.qty ?? row.quantity);
  return oq > 0 ? oq : 0;
}

export const entryPriceOf = (row: PnlRow): number => num(row.entryPrice ?? row.entry_price);

/**
 * Running mark-to-market of ONE open row against a live price.
 * Returns null when the row is not open, has no entry, or `ltp` is not a
 * positive number — "unknown", which the UI must show as "—", never ₹0.
 */
export function runningM2M(row: PnlRow, ltp: number | null | undefined): number | null {
  const oq = openQtyOf(row);
  const entry = entryPriceOf(row);
  if (oq <= 0 || entry <= 0) return null;
  const px = Number(ltp);
  if (!Number.isFinite(px) || px <= 0) return null;
  return (px - entry) * oq * dirOf(row);
}

export const isClosedRow = (row: PnlRow): boolean =>
  ['WIN', 'LOSS', 'BREAKEVEN'].includes(up(row.status)) && !row.voided;

/** Realised (booked) P&L of closed rows. netPnl is charges-inclusive and wins
 *  over gross `pnl`; a voided row is quarantined and contributes nothing. */
export function bookedPnl(rows: PnlRow[]): number {
  let s = 0;
  for (const r of rows) if (isClosedRow(r)) s += num(r.netPnl ?? r.pnl);
  return s;
}

/** Sum of M2M across open rows. `priced` counts rows that had a price; a
 *  `null` sum means NOT ONE open row could be marked. */
export function sumM2M(rows: PnlRow[], ltpOf: (row: PnlRow) => number | null | undefined)
  : { m2m: number | null; priced: number; open: number } {
  let m2m: number | null = null, priced = 0, open = 0;
  for (const r of rows) {
    if (openQtyOf(r) <= 0) continue;
    open += 1;
    const v = runningM2M(r, ltpOf(r));
    if (v == null) continue;
    priced += 1;
    m2m = (m2m ?? 0) + v;
  }
  return { m2m, priced, open };
}

/** Total = booked + running. A missing running figure leaves total = booked
 *  and the caller says so ("no live tick on N legs"). */
export const totalPnl = (booked: number, m2m: number | null): number => booked + (m2m ?? 0);

// ── contract identity & levels (display) ────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09-29" → "29-Sep"; anything else is returned as-is (never invented). */
export function expiryShort(exp?: string | null): string {
  const s = String(exp || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  return `${m[3]}-${MONTHS[Number(m[2]) - 1] || m[2]}`;
}

export interface ContractParts {
  kind: AssetKind;
  underlying: string;      // display: "NIFTY 50", "BANK NIFTY", "SILVERM"
  underlyingKey: string;   // ledger spelling: "NIFTY_50"
  strike: number;
  optType: string;         // CE | PE | ''
  expiry: string;          // ISO if known, '' otherwise
  expiryShort: string;
  /** "NIFTY 50 · 23250 CE · 29-Sep" / "SILVERM · FUT · 30-Nov" / "RELIANCE · CASH" */
  label: string;
}

/**
 * The parts of the contract a row traded, for the asset column. The underlying
 * is always named beside the strike so "23250 CE" never floats without its
 * index. Expiry comes from the row's own `expiry`, then the contractSymbol's
 * embedded date; when neither exists the label simply omits it.
 */
export function contractParts(row: PnlRow): ContractParts {
  const kind = assetKindOf(row);
  const os = String(row.optionSymbol || row.option_symbol || '').trim();
  const m = /^(.+?)\s+(\d+(?:\.\d+)?)\s+(CE|PE)$/i.exec(os);
  // `symbol` is the underlying on ledger rows; /trades/open hands the option
  // string there instead, so an option-shaped symbol is stripped to its root.
  const symRaw = up(row.symbol);
  const symOpt = /^(.+?)\s+\d+(?:\.\d+)?\s+(CE|PE)$/.exec(symRaw);
  const underlyingKey = (symOpt ? symOpt[1] : symRaw) || (m ? m[1].toUpperCase() : '') || up(os).split(/\s+/)[0] || '?';
  const underlying = underlyingKey.replace(/_/g, ' ');
  const strike = num(row.strike) || (m ? Number(m[2]) : 0);
  const optType = up(row.optType) || (m ? m[3].toUpperCase() : '');
  let expiry = String(row.expiry || row.expiryDate || '').trim();
  if (!expiry) {
    const cm = /(\d{4}-\d{2}-\d{2})/.exec(String(row.contractSymbol || ''));
    if (cm) expiry = cm[1];
  }
  const exS = expiryShort(expiry);
  const mid = kind === 'OPTION'
    ? (strike ? `${strike} ${optType}`.trim() : optType || 'OPT')
    : kind === 'FUTURE' ? 'FUT' : 'CASH';
  const label = [underlying, mid, exS].filter(Boolean).join(' · ');
  return { kind, underlying, underlyingKey, strike, optType, expiry, expiryShort: exS, label };
}

export interface RowLevels {
  entry: number;
  /** The row's average entry side: a written option's entry IS its average sell. */
  avgBuy: number | null;
  avgSell: number | null;
  /** Stop in PREMIUM space (options only, when the ledger stamped it). */
  slPremium: number | null;
  slPremiumBasis: string;
  /** Stop / target on the UNDERLYING (dnaSl / dnaTarget). */
  slUnderlying: number | null;
  targetUnderlying: number | null;
}

/**
 * Entry averages and the structural levels, each in its own currency and
 * named as such. `dnaSlPremium` is the same stop as `dnaSl` expressed in the
 * option's premium (routes_trading._sl_premium_fields); there is NO premium
 * target in the ledger, so the target is only ever an underlying level.
 */
export function levelsOf(row: PnlRow): RowLevels {
  const entry = entryPriceOf(row);
  const short = dirOf(row) === -1;
  const opt = assetKindOf(row) === 'OPTION';
  const slp = num(row.dnaSlPremium);
  const slu = num(row.dnaSl);
  const tgu = num(row.dnaTarget);
  const xp = num(row.exitPrice);
  const closed = isClosedRow(row);
  return {
    entry,
    avgBuy:  short ? (closed && xp > 0 ? xp : null) : (entry > 0 ? entry : null),
    avgSell: short ? (entry > 0 ? entry : null) : (closed && xp > 0 ? xp : null),
    slPremium: opt && slp > 0 ? slp : null,
    slPremiumBasis: String(row.dnaSlPremiumBasis || ''),
    slUnderlying: slu > 0 ? slu : null,
    targetUnderlying: tgu > 0 ? tgu : null,
  };
}

/**
 * The symbol(s) to subscribe on the shared /ws/live-feed for this row's OWN
 * contract — the option premium, the futures contract, the stock — never the
 * index spot. marketFeed normalises spaces/underscores, so "NIFTY_50 23250 CE"
 * and the spine's "NIFTY_23250_CE" meet on the same key. Futures are listed
 * under two spellings because the feed has used both (`_isFutSym` in
 * ChartPane accepts FUT and FUTCONTINUOUS).
 */
export function feedKeysOf(row: PnlRow): string[] {
  const p = contractParts(row);
  if (p.kind === 'OPTION') {
    const os = String(row.optionSymbol || row.option_symbol || '').trim();
    if (/\s(CE|PE)$/i.test(os)) return [os];
    return p.strike ? [`${p.underlyingKey} ${p.strike} ${p.optType}`] : [];
  }
  if (p.kind === 'FUTURE') return [`${p.underlyingKey} FUT`, `${p.underlyingKey} FUT CONTINUOUS`];
  return [p.underlyingKey];
}

// ── executable spec ──────────────────────────────────────────────────────────
//  Worked examples (also the table a reviewer can check by hand):
//   1. BUY CE  entry 88.35, openQty 120, ltp 92.10  → (92.10−88.35)×120 = +450.00
//   2. SELL CE entry 483,   openQty 30,  ltp 496.55 → (483−496.55)×30   = −406.50
//   3. SHORT FUT entry 237049, openQty 5, ltp 236900 → (237049−236900)×5 = +745
//   4. open row, ltp 0/undefined                   → null (never 0)
//   5. closed row (LOSS, openQty 0), any ltp        → null
//   6. booked of [WIN +100, LOSS −480.97, OPEN, VOID WIN +999] = −380.97
//   7. total(booked −380.97, m2m +450) = +69.03 ; total(−380.97, null) = −380.97
//   8. assetKindOf: {optType:'PE'}→OPTION · {optionSymbol:'SILVERM FUT',assetClass:''}→FUTURE ·
//      {symbol:'RELIANCE'}→CASH
//   9. contractParts({symbol:'NIFTY_50',optionSymbol:'NIFTY_50 23250 CE',strike:23250,optType:'CE',
//      expiry:'2026-09-29'}).label === 'NIFTY 50 · 23250 CE · 29-Sep'
export function pnlMathSelfCheck(): { ok: boolean; failures: string[] } {
  const f: string[] = [];
  const near = (a: number | null, b: number | null) =>
    a === b || (a != null && b != null && Math.abs(a - b) < 1e-6);
  const chk = (name: string, cond: boolean) => { if (!cond) f.push(name); };
  const buy: PnlRow = { status: 'OPEN', side: 'BUY', direction: 'LONG', optType: 'PE', openQty: 120, entryPrice: 88.35 };
  const sell: PnlRow = { status: 'OPEN', side: 'SELL', direction: 'SHORT', optType: 'CE', openQty: 30, entryPrice: 483 };
  const fut: PnlRow = { status: 'OPEN', side: 'SELL', direction: 'SHORT', optionSymbol: 'SILVERM FUT', assetClass: '', openQty: 5, entryPrice: 237049 };
  chk('1 buy option m2m', near(runningM2M(buy, 92.10), 450));
  chk('2 written option m2m', near(runningM2M(sell, 496.55), -406.5));
  chk('3 short future m2m', near(runningM2M(fut, 236900), 745));
  chk('4a ltp 0 → null', runningM2M(buy, 0) === null);
  chk('4b ltp undefined → null', runningM2M(buy, undefined) === null);
  chk('5 closed row → null', runningM2M({ ...sell, status: 'LOSS', openQty: 0 }, 500) === null);
  const booked = bookedPnl([
    { status: 'WIN', netPnl: 100 }, { status: 'LOSS', netPnl: -480.97 },
    { status: 'OPEN', netPnl: 55 }, { status: 'WIN', netPnl: 999, voided: true },
  ]);
  chk('6 booked', near(booked, -380.97));
  chk('7a total with m2m', near(totalPnl(-380.97, 450), 69.03));
  chk('7b total null m2m', near(totalPnl(-380.97, null), -380.97));
  chk('8a kind option', assetKindOf({ optType: 'PE' }) === 'OPTION');
  chk('8b kind future', assetKindOf(fut) === 'FUTURE');
  chk('8c kind cash', assetKindOf({ symbol: 'RELIANCE' }) === 'CASH');
  chk('9 label', contractParts({ symbol: 'NIFTY_50', optionSymbol: 'NIFTY_50 23250 CE', strike: 23250, optType: 'CE', expiry: '2026-09-29' }).label
    === 'NIFTY 50 · 23250 CE · 29-Sep');
  const s = sumM2M([buy, sell, { ...buy, id: 'x' }], r => (r === sell ? 496.55 : r === buy ? 92.10 : null));
  chk('10 sumM2M', near(s.m2m, 43.5) && s.priced === 2 && s.open === 3);
  chk('11 levels written', levelsOf(sell).avgSell === 483 && levelsOf(sell).avgBuy === null);
  return { ok: f.length === 0, failures: f };
}

// DEV-only console hook; tree-shaken out of production builds.
try {
  if ((import.meta as any).env?.DEV && typeof window !== 'undefined') {
    (window as any).__pnlMathSelfCheck = pnlMathSelfCheck;
  }
} catch { /* non-browser */ }
